import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import styles from './Field.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: ReactNode;
  /** When set, the field renders invalid and the message is announced. */
  error?: string | undefined;
  /** Rendered inside the field, at the trailing edge (PasswordField uses it). */
  action?: ReactNode;
  id?: string;
}

export function Input({ label, hint, error, action, id, className, ...rest }: InputProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  const describedBy = [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>

      <div className={styles.wrap}>
        <input
          {...rest}
          id={inputId}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          className={[
            styles.input,
            error !== undefined ? styles.invalid : undefined,
            action !== undefined ? styles.withAction : undefined,
            className,
          ]
            .filter(Boolean)
            .join(' ')}
        />
        {action}
      </div>

      {hint !== undefined ? (
        <span className={styles.hint} id={hintId}>
          {hint}
        </span>
      ) : null}

      {/* `role="alert"` so a validation failure is spoken when it appears, not
          only when the field is next focused. */}
      {error !== undefined ? (
        <span className={styles.error} id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
